import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Visibility from '../../components/Visibility';
import { Image } from '../../components/layouts';
import Sequence from '../../components/sequence';
import { debounce } from 'lodash';
import './style.scss';
import { buildImageURL, getSeqFrames, getTrack, pureTrans } from '../../helpers';
import { AppContext } from '../../Context';

const HERO_TEXT_D = buildImageURL('/hero-text-d.png');
const HERO_TEXT_M = buildImageURL('/hero-text-m.png');
const HERO_TEXT_PLACEHOLDER = buildImageURL('/hero-text-placeholder.svg');

const track = getTrack({ block: 1 });

export function useInterval(callback, delay, start) {
  const savedCallback = useRef();

  useEffect(() => {
    if (!start) {
      return;
    }
    savedCallback.current = callback;
  }, [callback, start]);

  useEffect(() => {
    if (!start) {
      return;
    }

    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay, start]);
}

const roll = setIndex =>
  debounce(function(indexDrawn) {
    setIndex(index => {
      if (index > indexDrawn) {
        return index;
      }
      if (indexDrawn === FRAME_LENGTH - 1) {
        // return 0;
        return indexDrawn;
      }
      return indexDrawn + 1;
    });
  }, 41);

const FRAME_LENGTH = 111;

export function Hero({ mobile = false }) {
  const [frozen, freeze] = useState(true);
  const [onScreen, setOnScreen] = useState(false);
  const [framesLoaded, setFramesLoaded] = useState(false);
  const [index, setIndex] = useState(-1);

  const { webpSupport, webpDetectionFinished } = React.useContext(AppContext);

  const frames = useMemo(() => {
    return getSeqFrames({
      prefix: 'hero',
      mobile,
      webpSupport: false,
      len: FRAME_LENGTH
    });
  }, [webpSupport, mobile]);

  useEffect(() => {
    if (onScreen && framesLoaded && index === -1) {
      setTimeout(() => {
        setIndex(0);
      }, 1000);
    }
  }, [framesLoaded, onScreen, index, setIndex]);

  const { a1 } = useMemo(
    () => ({
      a1: pureTrans(index, [76, FRAME_LENGTH - 1], [0, 1], 3)
    }),
    [index]
  );

  const onDrawn = useCallback(roll(setIndex), [setIndex]);

  const handleUpdate = useCallback(
    (e, { calculations }) => {
      const { onScreen, pixelsPassed, height, offScreen } = calculations;
      freeze(offScreen || pixelsPassed <= -1000 || pixelsPassed >= height + 1000);
      setOnScreen(onScreen);
    },
    [setOnScreen, freeze]
  );

  const onFramesLoaded = useCallback(() => {
    setFramesLoaded(true);
  }, [setFramesLoaded]);

  const placeholderVisible = !framesLoaded;
  const opacity = placeholderVisible ? 0 : a1;

  const seqReady = !frozen && webpDetectionFinished;
  return (
    <Visibility fireOnMount onUpdate={handleUpdate} className="page-section inverted section-1" data-track={track}>
      <div className="hero-text-placeholder-wrapper">
        <div className="trans" data-visible={!framesLoaded}>
          <Image src={HERO_TEXT_PLACEHOLDER} className="hero-text-placeholder" />
        </div>
      </div>
      <div className="section-content">
        <div className="trans" style={{ opacity }}>
          <Image src={mobile ? HERO_TEXT_M : HERO_TEXT_D} className="hero-text" />
        </div>
      </div>
      <div className="section-background trans delay" data-visible={!placeholderVisible}>
        <div className="sequence-wrapper">
          <Sequence current={index} frames={frames} onload={onFramesLoaded} onDrawn={onDrawn} active={seqReady} />
        </div>
      </div>
      {/* <div
        className="sequence-placeholder trans"
        data-visible={placeholderVisible}
        style={{ backgroundImage: `url(${frames[frames.length - 1]})` }}
      /> */}
    </Visibility>
  );
}
